import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/navigation';
import { Button } from '@/components/ui/button';
import { Footer } from '@/components/footer';
import { PageBackground } from '@/components/ui/page-background';
import { PageHero } from '@/components/ui/page-hero';
import { ArrowRight, Calendar, Send, Loader2 } from 'lucide-react';
import { SEO } from '@/components/seo';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import * as z from 'zod';

const formSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
  company: z.string().trim().max(100, 'Company name must be less than 100 characters').optional(),
  plan: z.string().optional(),
  timeline: z.string().optional(),
  goals: z.string().trim().min(10, 'Please describe your goals (at least 10 characters)').max(2000, 'Goals must be less than 2000 characters'),
});

type FormData = z.infer<typeof formSchema>;

const Contact = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const preselectedPlan = urlParams.get('plan');
  const preselectedService = urlParams.get('service');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    plan: preselectedPlan || preselectedService || '',
    timeline: '',
    goals: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    if (preselectedPlan) {
      setFormData(prev => ({ ...prev, plan: preselectedPlan }));
    }
    if (preselectedService) {
      setFormData(prev => ({ ...prev, plan: preselectedService }));
    }
  }, [preselectedPlan, preselectedService]);

  const validateForm = (): boolean => {
    try {
      formSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please check the form for errors",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: formData,
      });

      if (error) {
        throw error;
      }

      console.log('Form submitted successfully:', data);
      
      // Navigate to thank you page with plan info
      navigate(`/thank-you${formData.plan ? `?plan=${encodeURIComponent(formData.plan)}` : ''}`);
    } catch (error: any) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <PageBackground>
      <SEO
        title="Contact Us | AffinityAim"
        description="Get in touch with our team. We'll reply within 24 hours."
        canonical="https://www.affinityaim.com/contact"
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact AffinityAim",
          "description": "Contact page for AffinityAim web development services"
        }}
      />
      <Navigation />

      <main id="main-content">
        <PageHero 
          label="CONTACT"
          title="Let's discuss"
          titleAccent="your project"
          description="Choose the best way to start: book a quick call or send us your project details."
        />

        {/* Two-Path Contact */}
        <section className="pb-20">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Path 1: Book a Call */}
                <div className="bg-surface/50 border border-border rounded-2xl p-8 hover:border-primary/20 transition-colors duration-300">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                      <Calendar className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="font-display font-semibold text-foreground mb-4 text-2xl">
                      Book a 15-minute call
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Quick chat to understand your goals and see if we're a good fit.
                    </p>
                    
                    <div className="bg-background/50 rounded-xl p-8 mb-6 border border-border/50">
                      <div className="text-center text-muted-foreground">
                        <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="mb-4">Calendar integration coming soon</p>
                        <p className="text-sm">For now, email us to schedule: <a href="mailto:post@affinityaim.com" className="text-primary hover:underline">post@affinityaim.com</a></p>
                      </div>
                    </div>

                    <div className="text-sm text-muted-foreground space-y-2">
                      <p>• No pressure - 15 minutes</p>
                      <p>• Discuss timeline and approach</p>
                      <p>• Get a rough estimate</p>
                    </div>
                  </div>
                </div>

                {/* Path 2: Send Details */}
                <div className="bg-surface/50 border border-border rounded-2xl p-8 hover:border-primary/20 transition-colors duration-300">
                  <div className="mb-8">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                      <Send className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="font-display font-semibold text-foreground mb-4 text-2xl">
                      Send us your details
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Share your project info and we'll respond within 24 hours.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-name">
                          Name *
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className={`w-full px-4 py-3 bg-background border ${errors.name ? 'border-red-500' : 'border-border'} rounded-lg text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                          placeholder="Your name"
                        />
                        {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-email">
                          Email *
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className={`w-full px-4 py-3 bg-background border ${errors.email ? 'border-red-500' : 'border-border'} rounded-lg text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                          placeholder="your@email.com"
                        />
                        {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-company">
                        Company
                      </label>
                      <input
                        id="contact-company"
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                        placeholder="Company name"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-plan">
                          Which plan interests you?
                        </label>
                        <select
                          id="contact-plan"
                          value={formData.plan}
                          onChange={(e) => handleInputChange('plan', e.target.value)}
                          className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                        >
                          <option value="">Select plan</option>
                          <option value="Website Essentials">Website Essentials ($500/year)</option>
                          <option value="Growth Website + SEO">Growth Website + SEO ($800/year)</option>
                          <option value="AI Chatbot">AI Chatbot ($500/year)</option>
                          <option value="Website + SEO + Chatbot">Website + SEO + Chatbot ($1,000/year)</option>
                          <option value="Custom App / Web App / MVP">Custom App / Web App / MVP (Custom)</option>
                          <option value="technical-review">Technical Review</option>
                          <option value="conversion-analysis">Conversion Analysis</option>
                          <option value="mvp-planning">MVP Planning</option>
                          <option value="help-decide">Help me decide</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-timeline">
                          Timeline
                        </label>
                        <select
                          id="contact-timeline"
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                        >
                          <option value="">Select timeline</option>
                          <option value="asap">ASAP</option>
                          <option value="1-2-months">1-2 months</option>
                          <option value="3-6-months">3-6 months</option>
                          <option value="6+-months">6+ months</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-goals">
                        What are your goals? *
                      </label>
                      <textarea
                        id="contact-goals"
                        required
                        rows={4}
                        value={formData.goals}
                        onChange={(e) => handleInputChange('goals', e.target.value)}
                        className={`w-full px-4 py-3 bg-background border ${errors.goals ? 'border-red-500' : 'border-border'} rounded-lg text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none transition-all`}
                        placeholder="Tell us about your project goals, challenges, or what you'd like to build..."
                      />
                      {errors.goals && <p className="text-xs text-red-400 mt-1">{errors.goals}</p>}
                    </div>

                    <Button 
                      type="submit"
                      variant="default" 
                      size="lg" 
                      disabled={isSubmitting}
                      className="w-full group"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send details
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-muted-foreground text-center">
                      We'll reply within 24 hours from post@affinityaim.com.
                    </p>

                  </form>
                </div>

              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </PageBackground>
  );
};

export default Contact;
